import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { questions, categories, categorySlugs } from '../data/quizData';
import EmailGate from './EmailGate';

const { FiChevronLeft, FiChevronRight, FiCheck } = FiIcons;

const QuizPage = ({
  participantData,
  setParticipantData,
  setQuizResults
}) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showEmailGate, setShowEmailGate] = useState(false);

  const questionsPerPage = 8;
  const totalPages = Math.ceil(questions.length / questionsPerPage);
  const currentQuestions = questions.slice(
    currentPage * questionsPerPage,
    (currentPage + 1) * questionsPerPage
  );

  useEffect(() => {
    if (!participantData.name) {
      navigate('/');
    }
  }, [participantData.name, navigate]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage, showEmailGate]);

  const handleAnswerChange = (questionIndex, value) => {
    setAnswers((previous) => ({
      ...previous,
      [questionIndex]: parseInt(value, 10)
    }));
  };

  const getProgress = () => (
    (Object.keys(answers).length / questions.length) * 100
  );

  const isPageComplete = () => {
    const pageQuestionIndices = currentQuestions.map((question) => question.id - 1);
    return pageQuestionIndices.every((index) => answers[index] !== undefined);
  };

  const calculateResults = () => {
    const categoryScores = {};

    Object.keys(categories).forEach((category) => {
      categoryScores[category] = 0;
    });

    questions.forEach((question, index) => {
      categoryScores[question.category] += answers[index] || 0;
    });

    const allScores = Object.entries(categoryScores)
      .sort(([, first], [, second]) => second - first)
      .map(([category, score]) => ({
        category,
        score,
        slug: categorySlugs[category]
      }));

    return {
      categoryScores,
      topThree: allScores.slice(0, 3),
      allScores
    };
  };

  const handleSubmit = () => {
    if (Object.keys(answers).length === questions.length) {
      setShowEmailGate(true);
    }
  };

  const handleEmailSubmit = async ({ email, name }) => {
    const results = calculateResults();
    const lead = {
      email,
      quiz: 'dad-mind-fuck',
      quiz_result: results.topThree[0].slug,
      page: window.location.pathname
    };

    if (name) {
      lead.name = name;
    }

    const response = await fetch('https://pages.mfdads.com/api/lead', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(lead)
    });

    let responseBody = null;

    try {
      responseBody = await response.json();
    } catch {
      throw new Error('We could not verify your submission. Please try again.');
    }

    if (!response.ok || !responseBody || responseBody.ok !== true) {
      throw new Error(
        'We could not unlock your results right now. Please check your details and try again.'
      );
    }

    setParticipantData({
      ...participantData,
      email,
      name: name || participantData.name,
      consent: true
    });
    setQuizResults(results);
    navigate('/results');
  };

  if (showEmailGate) {
    return (
      <EmailGate
        onSubmit={handleEmailSubmit}
        name={participantData.name}
      />
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen p-2 sm:p-4"
    >
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-t-2xl p-4 sm:p-6 shadow-lg">
          <div className="flex justify-between items-center mb-3">
            <h1 className="text-lg sm:text-2xl font-bold text-midnight-blue">
              Dad-Mind-Fucks Assessment
            </h1>
            <span className="text-sm sm:text-base text-md-blue font-semibold">
              {currentPage + 1}/{totalPages}
            </span>
          </div>

          <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3 mb-2">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${getProgress()}%` }}
              transition={{ duration: 0.5 }}
              className="bg-yellow h-2 sm:h-3 rounded-full"
            />
          </div>

          <p className="text-xs sm:text-sm text-gray-600 text-center">
            {Object.keys(answers).length} of {questions.length} completed
          </p>
        </div>

        <div className="bg-white p-4 sm:p-6 shadow-lg">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentPage}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
              className="space-y-6 sm:space-y-8"
            >
              {currentQuestions.map((question) => (
                <div
                  key={question.id}
                  className="border-b border-gray-200 pb-4 sm:pb-6 last:border-b-0"
                >
                  <h3 className="text-sm sm:text-lg font-semibold text-midnight-blue mb-3 sm:mb-4">
                    {question.id}. {question.text}
                  </h3>

                  <div className="grid grid-cols-5 gap-1 sm:gap-3">
                    {[
                      [1, 'Not at all'],
                      [2, 'A little'],
                      [3, 'Somewhat'],
                      [4, 'Quite a bit'],
                      [5, 'Very much']
                    ].map(([value, label]) => (
                      <label
                        key={value}
                        className={`cursor-pointer border-2 rounded-lg p-2 sm:p-4 text-center transition-all hover:shadow-md ${
                          answers[question.id - 1] === value
                            ? 'border-yellow bg-yellow bg-opacity-10 text-midnight-blue'
                            : 'border-gray-300 hover:border-md-blue'
                        }`}
                      >
                        <input
                          type="radio"
                          name={`question-${question.id}`}
                          value={value}
                          checked={answers[question.id - 1] === value}
                          onChange={(event) => handleAnswerChange(
                            question.id - 1,
                            event.target.value
                          )}
                          className="sr-only"
                        />
                        <div className="font-bold text-sm sm:text-lg">{value}</div>
                        <div className="text-xs sm:text-base leading-tight">{label}</div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="bg-white rounded-b-2xl p-4 sm:p-6 shadow-lg">
          <div className="flex justify-between items-center">
            <button
              type="button"
              onClick={() => setCurrentPage((page) => page - 1)}
              disabled={currentPage === 0}
              className={`flex items-center gap-2 px-4 sm:px-6 py-3 rounded-lg font-semibold ${
                currentPage === 0
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-md-blue text-white hover:bg-opacity-90'
              }`}
            >
              <SafeIcon icon={FiChevronLeft} />
              <span>Previous</span>
            </button>

            {currentPage === totalPages - 1 ? (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={Object.keys(answers).length !== questions.length}
                className={`flex items-center gap-2 px-5 sm:px-8 py-3 rounded-lg font-bold ${
                  Object.keys(answers).length === questions.length
                    ? 'bg-yellow text-midnight-blue hover:bg-opacity-90'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                <SafeIcon icon={FiCheck} />
                <span>Continue to My Results</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setCurrentPage((page) => page + 1)}
                disabled={!isPageComplete()}
                className={`flex items-center gap-2 px-5 sm:px-6 py-3 rounded-lg font-semibold ${
                  isPageComplete()
                    ? 'bg-yellow text-midnight-blue hover:bg-opacity-90'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                <span>Next</span>
                <SafeIcon icon={FiChevronRight} />
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default QuizPage;